import {
  ButtonERP,
  DataTablePrimeReact,
  DropdownPrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { ContactoReadingDtoProps } from "api/DTOs/ContactoReadingDto"
import { FacturaReadingFiltersDtoProps } from "api/DTOs/FacturaReadingFiltersDto"
import { FacturaReadingSimpleDtoProps } from "api/DTOs/FacturaReadingSimpleDto"
import {
  FacturasByFilterQueryParamsProps,
  getFacturasByFilter,
  getFacturasByIdSimple,
} from "api/Factura.service"
import { TIPO_DOCUMENTO_ENUM } from "api/Types/TipoDocumentoEnum"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import axios from "axios"
import ModalVerDocumento from "common/components/ModalVerDocumento/ModalVerDocumento"
import NoDataTemplate from "common/components/NoDataTemplate"
import { STATIC_ASSETS_URL } from "common/context/constants"
import useCrearDocumento from "common/hooks/useEnviarDocumento"
import { useRectificarFactura } from "common/hooks/useRectificarOEditarFactura"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import {
  OPTIONS_DOC,
  OPTIONS_FACTURA_CONTACTO,
} from "features/Contactos/constants"
import PagoCobroManualForm from "features/Documentos/components/ModalPagoCobroManual/ModalPagoCobroManual"
import ModalTypeDownloadDocument from "features/Documentos/components/ModalTypeDownloadDocument/ModalTypeDownloadDocument"
import { ModalTypeDownloadDocumentProps } from "features/Documentos/components/ModalTypeDownloadDocument/constant"
import { COLUMNAS_FACTURAS_ENVIADAS_ERP } from "features/Documentos/pages/Facturas/FacturasEnviadas/constants/FacturasEnviadasColumns"
import { COLUMNAS_FACTURAS_RECIBIDAS_ERP } from "features/Documentos/pages/Facturas/FacturasRecibidas/constants/FacturasRecibidasColumns"
import { useFilterMenu } from "hooks/useFilterMenu"
import { omit } from "lodash"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import FilterMenu from "./FilterMenu"

const FacturasContatos = ({
  contacto,
}: {
  contacto: ContactoReadingDtoProps
}) => {
  const { t } = useTranslation()
  const history = useHistory()

  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [showModalPagoCobroManual, setShowModalPagoCobroManual] = useState<{
    show: boolean
    id: string | undefined
    serie: string | null | undefined
  }>({ show: false, id: undefined, serie: undefined })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)

  const dtRef = useRef<DataTablePrimeReact<
    FacturaReadingFiltersDtoProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }

  const [tableSelected, setTableSelected] = useState(OPTIONS_DOC.VENTA)
  const [showModalDownload, setShowModalDownload] = useState<
    ModalTypeDownloadDocumentProps["modalState"]
  >({
    show: false,
    id: null,
    serie: null,
  })

  const { activeCompany: empresaActiva } = useUsuarioEmpresaActiva()
  const { crearDocumentoConLibrado } = useCrearDocumento()
  const { rectificarOeditarFactura } = useRectificarFactura()
  const [modalDocumentoDetail, setModalDocumentoDetail] = useState<{
    show: boolean
    document: FacturaReadingSimpleDtoProps | null
  }>({
    show: false,
    document: null,
  })

  const ventasCancelToken = useRef(axios.CancelToken.source())
  const comprasCancelToken = useRef(axios.CancelToken.source())
  const fetchItemsVentas = useCallback(() => {
    return (queryState: FacturasByFilterQueryParamsProps) => {
      if (!contacto.nif) return
      ventasCancelToken.current?.cancel()
      ventasCancelToken.current = axios.CancelToken.source()
      return getFacturasByFilter(
        {
          ...queryState,
          MaxResultCount: queryState.MaxResultCount ?? 100,
          SortingCriteria:
            queryState.SortingCriteria ??
            "FechaEmision DESC, CreationTime DESC",
          ClienteNif: contacto.nif,
          LibradorId: empresaActiva.id,
        },
        ventasCancelToken.current.token
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    items: itemsVentas,
    isLoading: isLoadingVentas,
    totalCount: totalCountVentas,
    setServiceQuery: setServiceQueryVentas,
    fetchTableData: fetchTableDataVentas,
  } = useFilterMenu({
    serviceCall: fetchItemsVentas,
  })

  const fetchItemsCompras = useCallback(() => {
    return (queryState: FacturasByFilterQueryParamsProps) => {
      if (!contacto.nif) return
      comprasCancelToken.current?.cancel()
      comprasCancelToken.current = axios.CancelToken.source()
      return getFacturasByFilter(
        {
          ...queryState,
          MaxResultCount: queryState.MaxResultCount ?? 100,
          SortingCriteria:
            queryState.SortingCriteria ??
            "FechaEmision DESC, CreationTime DESC",
          ProveedorNif: contacto.nif,
          LibradoId: empresaActiva.id,
        },
        comprasCancelToken.current.token
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    items: itemsCompras,
    isLoading: isLoadingCompras,
    totalCount: totalCountCompras,
    setServiceQuery: setServiceQueryCompras,
    fetchTableData: fetchTableDataCompras,
  } = useFilterMenu({
    serviceCall: fetchItemsCompras,
  })

  useEffect(() => {
    history.location.state?.selectedSubTab &&
      setTableSelected(history?.location.state.selectedSubTab)
  }, [history.location.state?.selectedSubTab])

  const handleOpenModalDocumento = async (id: string) => {
    const facturaById = await getFacturasByIdSimple(id)
    setModalDocumentoDetail({
      show: true,
      document: facturaById,
    })
  }
  const showFinanciadaColumn =
    tableSelected === OPTIONS_DOC.VENTA
      ? itemsVentas.some((item: FacturaReadingFiltersDtoProps) =>
          Boolean(
            item.estadoOperaciones?.estadoFinanciacionEfecto ||
              item.estadoOperaciones?.estadoOperacion
          )
        )
      : false

  return (
    <section className='tw-flex tw-flex-col tw-h-full'>
      <div className='tw-flex tw-flex-row tw-w-full tw-justify-between tw-px-6 tw-py-3'>
        <DropdownPrimeReact
          options={OPTIONS_FACTURA_CONTACTO(t, {
            ventas: totalCountVentas,
            compras: totalCountCompras,
          })}
          optionLabel='name'
          optionValue='value'
          onChange={(e) => {
            setTableSelected(e.value)
          }}
          value={tableSelected}
          pt={{
            root: {
              className: "tw-bg-transparent tw-border-none",
            },
            input: {
              className: "tw-text-2xl",
            },
          }}
        />

        <span className='tw-flex tw-flex-row tw-gap-3 tw-items-center'>
          <FilterMenu
            tableSelected={tableSelected}
            serviceQuery={(
              query: Partial<FacturasByFilterQueryParamsProps>
            ) => {
              const queryVentas = omit(query, ["EstadosPago"])
              const queryCompras = omit(query, ["EstadosCobro"])
              setServiceQueryVentas(queryVentas)
              setServiceQueryCompras(queryCompras)
            }}
            sortingCriteria={sortingCriteria}
            maxResultCount={pagination.itemsPerPage}
            skipCount={pagination.skipCount}
            resetPagination={handleResetPagination}
          />
          {tableSelected === OPTIONS_DOC.VENTA ? (
            <ButtonERP
              customize={{
                type: "secondary",
                size: "md",
              }}
              label={`${t("crear.factura.addOrder")}`}
              onClick={() =>
                crearDocumentoConLibrado(
                  ROUTES.CREAR_FACTURA,
                  contacto,
                  empresaActiva
                )
              }
            />
          ) : null}
        </span>
      </div>
      <div className='tw-flex tw-flex-col tw-h-[calc(100%-72px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)]'>
          <TableERP
            refTable={dtRef}
            columns={
              tableSelected === OPTIONS_DOC.VENTA
                ? COLUMNAS_FACTURAS_ENVIADAS_ERP({
                    t,
                    setShowModalDownload,
                    setShowModalPagoCobroManual,
                    rectificarOeditarFactura,
                    showFinanciadaColumn,
                    showContacto: false,
                  })
                : COLUMNAS_FACTURAS_RECIBIDAS_ERP({
                    t,
                    setShowModalDownload,
                    setShowModalPagoCobroManual,
                    showContacto: false,
                  })
            }
            className='tw-h-full !tw-z-0'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={
              tableSelected === OPTIONS_DOC.VENTA ? itemsVentas : itemsCompras
            }
            showGridlines
            lazy
            dataKey='id'
            scrollable
            selectionMode='checkbox'
            size='small'
            selection={[]}
            loading={
              tableSelected === OPTIONS_DOC.VENTA
                ? isLoadingVentas
                : isLoadingCompras
            }
            onRowClick={({ data }) => {
              if (!data.id) return
              handleOpenModalDocumento(data.id)
            }}
            emptyMessage={
              <NoDataTemplate
                icon={`${STATIC_ASSETS_URL}/Imagenes/Ilustraciones/tablas/facturas-de-${
                  tableSelected === OPTIONS_DOC.COMPRA ? "compra" : "venta"
                }.svg`}
                title={
                  tableSelected === OPTIONS_DOC.COMPRA
                    ? "crear.factura.buyLabel"
                    : "crear.factura.saleLabel"
                }
                description='shared.noDataDescription'
                actionLabel='crear.factura.addOrder'
                showActions={tableSelected === OPTIONS_DOC.VENTA}
                actionCommand={() =>
                  crearDocumentoConLibrado(
                    ROUTES.CREAR_FACTURA,
                    contacto,
                    empresaActiva
                  )
                }
              />
            }
          />
        </div>
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={
            tableSelected === OPTIONS_DOC.VENTA
              ? totalCountVentas
              : totalCountCompras
          }
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => {
            dtRef.current && dtRef.current?.resetScroll()
          }}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>

      <ModalTypeDownloadDocument
        modalState={showModalDownload}
        onHide={() =>
          setShowModalDownload({
            show: false,
            id: null,
            serie: null,
          })
        }
        showElements={{
          downloadPdf: true,
          downloadXml: true,
        }}
        typeDocument={TIPO_DOCUMENTO_ENUM.FACTURA}
      />

      <ModalVerDocumento
        showModal={modalDocumentoDetail.show}
        onHide={() =>
          setModalDocumentoDetail({
            show: false,
            document: null,
          })
        }
        docType={TIPO_DOCUMENTO_ENUM.FACTURA}
        refreshData={
          tableSelected === OPTIONS_DOC.VENTA
            ? fetchTableDataVentas
            : fetchTableDataCompras
        }
        documentoID={modalDocumentoDetail.document?.id ?? null}
        typeDocumentTable='facturas'
        isVenta={tableSelected === OPTIONS_DOC.VENTA}
      />
      <PagoCobroManualForm
        show={showModalPagoCobroManual.show}
        isVenta={tableSelected === OPTIONS_DOC.VENTA}
        onHide={() => {
          setShowModalPagoCobroManual({
            show: false,
            id: undefined,
            serie: undefined,
          })
        }}
        document={{
          serie: showModalPagoCobroManual.serie,
          id: showModalPagoCobroManual.id,
        }}
        t={t}
        updateData={
          tableSelected === OPTIONS_DOC.VENTA
            ? fetchTableDataVentas
            : fetchTableDataCompras
        }
      />
    </section>
  )
}

export default FacturasContatos
